import { KeyboardEvent, MouseEvent as ReactMouseEvent, useState } from 'react';
import classNames from 'classnames';

import { icon_chevron_down, icon_chevron_up } from '@gn-audio/icons';
import { getTitle } from '@gn-audio/jss-core';

import { useFallbackTranslation } from '../../../utils/hooks';
import { BasketButton } from '../../Basket/BasketButton';
import { EcomOnly } from '../../EcomOnly/EcomOnly';
import { FeatureToggle } from '../../FeatureToggle';
import { JabraFocusGroup, JabraFocusItem } from '../../JabraFocus';
import { JabraIcon } from '../../JabraIcon/JabraIcon';
import { LogoLink } from '../../LogoLink/LogoLink';
import { UipMenu } from '../../UipMenu';
import { MenuItems } from '../enums/menu-items.enum';
import { menuComponentsMap } from '../headerComponentsMapper';
import { DISPLAY_VERSIONS } from '../MegaMenuConstants';
import { SubmenuId, SubmenuItem } from '../megaMenuTypes';
import { useHeaderNavigation } from '../providers';

import styles from '../MegaMenu.module.css';

type Props = {
  isNavigationTransparent?: boolean;
  showNavigation?: boolean;
};

export const DesktopTopMenu = ({
  showNavigation,
  isNavigationTransparent,
}: Props): React.JSX.Element => {
  const { t } = useFallbackTranslation();

  const [focusedItem, setFocusedItem] = useState<SubmenuItem>();
  const { menuItems, onMenuItemClick, closeMenu, activeItemId, openMenuItem, route } =
    useHeaderNavigation();

  const sideBarItems = [];
  if (route?.placeholders?.['language-selector']) {
    sideBarItems.push({ id: 'language-selector', name: MenuItems.LanguageSelector });
  }
  if (route?.placeholders?.['search']) {
    sideBarItems.push({ id: 'search', name: MenuItems.Search });
  }

  const handleOpenMenuItem = (menuItem: SubmenuItem) => {
    if (openMenuItem) {
      openMenuItem(menuItem);
    }
  };

  const handleTriggerActivate = (menuItem: SubmenuItem, e: ReactMouseEvent) => {
    // Enter/Space on the trigger produce a click with detail === 0. Route keyboard
    // opens through openMenuItem so focus is moved into the dropdown (shouldFocus).
    const isKeyboardActivated = e.detail === 0;
    if (isKeyboardActivated && activeItemId !== menuItem.name) {
      handleOpenMenuItem(menuItem);
      return;
    }
    onMenuItemClick?.(menuItem);
  };

  const handleMenuKeydown = (e: KeyboardEvent) => {
    if (!focusedItem) {
      return;
    }
    if (e.key === 'ArrowDown') {
      if (activeItemId === focusedItem.name) {
        return;
      }
      if (focusedItem) {
        handleOpenMenuItem(focusedItem);
      }
      e.preventDefault();
      e.stopPropagation();
    }
    if (e.key === 'ArrowUp') {
      closeMenu();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  return (
    <JabraFocusGroup
      onKeyDown={handleMenuKeydown}
      orientation="horizontal"
      className="hidden w-full lg:block"
      id="top-menu"
    >
      <div
        className={`h-16 w-full grid-cols-3 items-center px-3 lg:grid min-[1930px]:px-0 ${
          DISPLAY_VERSIONS.DESKTOP
        } topMenu ${isNavigationTransparent ? 'bg-transparent' : 'bg-white'}`}
      >
        <div>{<LogoLink />}</div>

        {showNavigation ? (
          <>
            <nav
              aria-label={t('topmenu.TopMenu')}
              className="top-menu flex items-center justify-center"
            >
              <ul className="flex">
                {menuItems.map((menuItem, index) => {
                  const isActive = activeItemId === menuItem.name;
                  const hasLink = menuItem.Link?.value?.href;

                  return (
                    <li
                      onClick={(e) => !hasLink && handleTriggerActivate(menuItem, e)}
                      className={classNames(
                        `mx-2 box-border text-nowrap px-1 text-[0.875rem] leading-6 text-black`,
                        {
                          'cursor-pointer': !hasLink,
                        }
                      )}
                      key={`${index}-${menuItem.name}`}
                    >
                      <JabraFocusItem onFocus={() => closeMenu()}>
                        {hasLink ? (
                          <a
                            href={menuItem.Link?.value?.href}
                            data-menu-trigger={menuItem.name}
                            onFocus={() => setFocusedItem(menuItem)}
                            onBlur={() => setFocusedItem(undefined)}
                            className="group flex items-center no-underline"
                            {...(menuItem.linkAriaLabel?.value && {
                              'aria-label': menuItem.linkAriaLabel.value,
                            })}
                          >
                            <span className="border-b-0 bg-gradient-to-r from-accent to-accent bg-[length:0_2px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 ease-in-out group-hover:bg-[length:100%_2px] group-hover:bg-left-bottom">
                              {getTitle(menuItem)}
                            </span>
                          </a>
                        ) : (
                          <button
                            data-menu-trigger={menuItem.name}
                            onFocus={() => setFocusedItem(menuItem)}
                            onBlur={() => setFocusedItem(undefined)}
                            className="group flex items-center"
                          >
                            <span
                              className={classNames(
                                `border-b-0 bg-gradient-to-r from-accent to-accent bg-[length:0_2px] bg-left-bottom bg-no-repeat transition-[background-size] duration-500 ease-in-out group-hover:bg-[length:100%_2px] group-hover:bg-left-bottom`,
                                {
                                  'bg-[length:100%_2px] bg-left-bottom': isActive,
                                }
                              )}
                            >
                              {getTitle(menuItem)}
                            </span>
                            <JabraIcon
                              className="ml-3 h-3 w-3"
                              Icon={isActive ? icon_chevron_up : icon_chevron_down}
                            />
                          </button>
                        )}
                      </JabraFocusItem>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <ul className="flex justify-self-end">
              {sideBarItems.map((menuItem, index) => {
                const isActiveSidebarItem = activeItemId === menuItem.name;
                return (
                  <JabraFocusItem
                    as="li"
                    key={`${index}-${menuItem.name}`}
                    onFocus={() => {
                      if (menuItem.name === MenuItems.LanguageSelector)
                        setFocusedItem(menuItem as SubmenuItem);
                    }}
                  >
                    <div
                      onClick={() => onMenuItemClick?.(menuItem as SubmenuItem)}
                      className={styles.menuButton}
                      onBlur={() => setFocusedItem(undefined)}
                    >
                      {menuComponentsMap[menuItem.name as SubmenuId]?.(
                        () => onMenuItemClick?.(menuItem as SubmenuItem),
                        isActiveSidebarItem
                      )}
                    </div>
                  </JabraFocusItem>
                );
              })}

              <EcomOnly>
                <FeatureToggle featureName="TopMenuUipEnabled">
                  <JabraFocusItem
                    as="li"
                    key="uip-icon"
                    className={classNames('flex justify-center', styles.menuButton)}
                  >
                    <UipMenu />
                  </JabraFocusItem>
                </FeatureToggle>
                <li className={styles.menuButton} key="basket">
                  <JabraFocusItem asChild>
                    <BasketButton />
                  </JabraFocusItem>
                </li>
              </EcomOnly>
            </ul>
          </>
        ) : null}
      </div>
    </JabraFocusGroup>
  );
};
